import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback handle the exchange first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (token) {
      // Verify token and get user info
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogleSession = async (sessionId) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/google/session`, {
        session_id: sessionId
      });
      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      return userData;
    } catch (error) {
      console.error('Google login failed:', error);
      return null;
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      if (response.data.mfa_required || response.data.mfa_setup_required) {
        return { mfa: response.data };
      }
      if (response.data.sms_required) {
        return { sms: response.data };
      }
      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      return userData;
    } catch (error) {
      console.error('Email login failed:', error);
      return null;
    }
  };

  // Complete an MFA login/setup step: adopt the issued session token
  const adoptSession = (newToken, userData) => {
    setToken(newToken);
    if (userData) setUser(userData);
    localStorage.setItem('token', newToken);
  };

  const registerWithEmail = async (email, password, name, ref = null) => {
    try {
      const signupSource = localStorage.getItem('signup_source');
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        name,
        ...(ref ? { ref } : {}),
        ...(signupSource ? { signup_source: signupSource } : {})
      });
      localStorage.removeItem('signup_source');

      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      return true;
    } catch (error) {
      console.error('Registration failed:', error);
      const detail = error?.response?.data?.detail;
      return { error: Array.isArray(detail) ? detail.map(d => d.msg || String(d)).join('. ') : (detail || 'Registration failed. Email may already be in use.') };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user:', error);
        return null;
      }
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, loginWithGoogleSession, loginWithEmail, registerWithEmail, logout, refreshUser, adoptSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
